import { JSX } from "react";
import { enumKeys, enumKeyOf } from "common/helpers/utils";
import {
  Resource,
  ResourceConstructor,
} from "permissions/entities/permission/resources/general";
import {
  CardType,
  GroupedCellConfig,
  TableRow
} from "permissions/entities/permission/view/components/permissions-table/types";
import { isPermissionsRow, isTemplateRow, isTopLevelParent } from "permissions/entities/permission/view/components/permissions-table/utils";
import { DocumentContentsAccess, DocumentSubjectMatter } from "permissions/entities/permission/resources/documents-resources";
import { type MRT_Row as Row } from "material-react-table";
import Guid from "common/values/guid/guid";

type TopLevelTemplates = {
  byConstructor: Map<ResourceConstructor, Resource>;
  legalByAccessType: Map<string, Resource>;
  docContentsBySubject: Map<string, Resource>;
};

type DocumentSubjectMatterGroupCellProps = {
  row: TableRow | null;
  topLevelTemplates: TopLevelTemplates;
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  canEdit: boolean;
  resources: Resource[];
  creatingRow: boolean | null;
  editingRow: Row<TableRow> | null;
  renderGroupedPermission: (
    row: TableRow,
    config: GroupedCellConfig
  ) => JSX.Element;
};

export default function DocumentSubjectMatterGroupCell(props: Readonly<DocumentSubjectMatterGroupCellProps>): JSX.Element | null {
  const {
    row,
    topLevelTemplates,
    isSingleGranteeView,
    isCreateTemplateView,
    isManageTemplatesView,
    canEdit,
    resources,
    creatingRow,
    editingRow,
    renderGroupedPermission
  } = props;

  if (!row) return null;
  return renderGroupedPermission(row, {
    cardType: CardType.Grouped,
    keys: enumKeys(DocumentSubjectMatter).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    ),
    templateForKey: (smKey) =>
      topLevelTemplates.docContentsBySubject.get(smKey),
    candidatesForKey: (row, smKey) => {
      if (isPermissionsRow(row)) {
        return row.recentPermissionsPreview.filter((permission) =>
          permission.resource instanceof DocumentContentsAccess
          && enumKeyOf(DocumentSubjectMatter, permission.resource.subjectMatter) === smKey
          && isTopLevelParent(permission)
        );
      }
      if (isTemplateRow(row)) {
        let source: Resource[];
        const isCreatingThisRow = creatingRow && row.id?.isEqualTo?.(Guid.empty);
        const isEditingThisRow =
          Boolean(editingRow?.original) && editingRow!.original.id?.isEqualTo?.(row.id);

        if (isCreatingThisRow || isEditingThisRow) {
          source = resources;
        } else {
          source = row.resources;
        }

        const filteredResources = source.filter((res) => res instanceof DocumentContentsAccess);
        return filteredResources.filter((res) =>
          enumKeyOf(DocumentSubjectMatter, res.subjectMatter) === smKey
        );
      }
      return [];
    },
    labelForKey: (smKey) => smKey,
    controlMetaForResource: () => null,
    title: (isSingleGranteeView || isCreateTemplateView || isManageTemplatesView)
      ? "Document Subject Matter" : undefined,
    colSpan: 1,
    rowSpan: 2,
    canEdit
  });
}
